import * as React from 'react';
import { SpaceProps } from 'styled-system';
import Section, { ISectionProps } from './Section/Section';
import Item, { IItemProps } from './Item/Item';
import Input, { IInputProps } from './Input/Input';
import Checkbox, { ICheckboxProps } from './Checkbox/Checkbox';
import Radiobox, { IRadioboxProps } from './Radiobox/Radiobox';
import RadioGroup, { IRadioGroupProps } from './RadioGroup/RadioGroup';
import CheckboxGroup, {
  ICheckboxGroupProps
} from './CheckboxGroup/CheckboxGroup';
import TextArea, { ITextAreaProps } from './TextArea/TextArea';
import Dropdown, { IDropdownProps } from './Dropdown/Dropdown';
import {
  SearchBox,
  SearchOption,
  SearchList,
  SelectAll
} from './SearchBox/SearchBox';
import {
  ISearchBoxProps,
  ISearchListProps,
  ISearchOptionProps,
  SelectAllProps
} from './SearchBox/types';
import { FormContext } from './context';
import StyledForm from './Form.style';

/**
 * Composition of composite components that the Form component exposes
 * @interface IFormComposition
 */
export interface IFormComposition {
  /**
   * Section component for containing related form items
   */
  Section: React.FC<ISectionProps>;
  /**
   * Sub-component of `Form` for wrapping form inputs.
   */
  Item: React.FC<IItemProps>;
  /**
   * Form input that renders an input of varying types
   */
  Input: React.FC<IInputProps>;
  /**
   * Form textarea
   */
  TextArea: React.FC<ITextAreaProps>;
  /**
   * Form input that renders a checkbox.
   * Can be used solo or within CheckboxGroup
   */
  Checkbox: React.FC<ICheckboxProps>;
  /**
   * Form input that renders a radio button.
   * Must be used within RadioboxGroup to render.
   */
  Radiobox: React.FC<IRadioboxProps>;
  /**
   * Sub-component of `Form` for wrapping radioboxes
   * in groups. Required for Radiobox to be used.
   */
  RadioGroup: React.FC<IRadioGroupProps>;
  /**
   * Sub-component of `Form` for wrapping
   * Checkboxes in groups when desired.
   */
  CheckboxGroup: React.FC<ICheckboxGroupProps>;
  /**
   * Sub-component of `Form` that wraps a search input
   * tobe used as part of a comboBox
   */
  SearchBox: React.FC<ISearchBoxProps>;
  /**
   * Popover list to be rendered under SearchBox.
   * Must be renderd as a child of SearchBox
   */
  SearchList: React.FC<ISearchListProps>;
  /**
   * Search option item to be rendered as a selection
   * in SearchBox.
   * Must be a decendent of SearchBox
   */
  SearchOption: React.FC<ISearchOptionProps>;
  /**
   * Select all option that appears in the SearchList.
   */
  SelectAll: React.FC<SelectAllProps>;
  /**
   * Form dropdown.
   */
  Dropdown: React.FC<IDropdownProps>;
}

/**
 * Props for the Form component
 *
 * @interface IFormProps
 */
export interface IFormProps
  extends React.FormHTMLAttributes<HTMLFormElement>,
    SpaceProps {
  /**
   * Sets prefixed ID for fields within form
   */
  name: string;
  /**
   * On submit event handler
   */
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
}

/**
 * Returns a form component to render Lumos custom forms
 *
 * @return Component to render form
 */
const Form: React.FC<IFormProps> & IFormComposition = ({
  children,
  name,
  ...rest
}) => {
  return (
    <FormContext.Provider value={{ name }}>
      <StyledForm {...rest}>{children}</StyledForm>
    </FormContext.Provider>
  );
};

Form.displayName = 'Form';
Form.defaultProps = {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  }
};

Form.Section = Section;
Form.Item = Item;
Form.Input = Input;
Form.TextArea = TextArea;
Form.Checkbox = Checkbox;
Form.Radiobox = Radiobox;
Form.RadioGroup = RadioGroup;
Form.CheckboxGroup = CheckboxGroup;
Form.Dropdown = Dropdown;

Form.SearchBox = SearchBox;
Form.SearchList = SearchList;
Form.SearchOption = SearchOption;
Form.SelectAll = SelectAll;

Form.Section.displayName = 'Form.Section';
Form.Item.displayName = 'Form.Item';
Form.Input.displayName = 'Form.Input';
Form.Checkbox.displayName = 'Form.Checkbox';
Form.Radiobox.displayName = 'Form.Radiobox';
Form.RadioGroup.displayName = 'Form.RadioGroup';
Form.CheckboxGroup.displayName = 'Form.CheckboxGroup';
Form.TextArea.displayName = 'Form.TextArea';
Form.Dropdown.displayName = 'Form.Dropdown';

Form.SearchBox.displayName = 'Form.SearchBox';
Form.SearchList.displayName = 'Form.SearchList';
Form.SearchOption.displayName = 'Form.SearchOption';
Form.SelectAll.displayName = 'Form.SelectAll';

export default Form;
